/**
 * Entitlement context — past-free / future-paid (web redesign Phase 2).
 *
 * Premium = local pro|scholar tier (Stripe later). Demo charts bypass every
 * gate via isDemoChart(chartId).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { getCurrentTier, setTier, type SubscriptionPlan } from "./subscription";
import {
  isDemoChart,
  isUnlocked as pureIsUnlocked,
} from "./entitlements";

interface EntitlementValue {
  isPremium: boolean;
  tier: "free" | "pro" | "scholar";
  loading: boolean;
  refresh: () => Promise<void>;
  /** Upgrade locally (preview billing). Stripe later. */
  setLocalTier: (tier: "free" | "pro" | "scholar") => Promise<void>;
  /**
   * The one gating rule. Content before today is free.
   * Demo charts always unlock.
   */
  isUnlocked: (contentDate: Date, chartId?: string | null) => boolean;
  paywallOpen: boolean;
  openPaywall: (previewCopy?: string) => void;
  closePaywall: () => void;
  paywallCopy: string;
}

const EntitlementContext = createContext<EntitlementValue | null>(null);

const DEFAULT_COPY =
  "The past was free. That was the proof. Unlock today and everything after.";

export function EntitlementProvider({ children }: { children: ReactNode }) {
  const [isPremium, setIsPremium] = useState(false);
  const [tier, setTierState] = useState<"free" | "pro" | "scholar">("free");
  const [loading, setLoading] = useState(true);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallCopy, setPaywallCopy] = useState(DEFAULT_COPY);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const t = await getCurrentTier();
      setTierState(t);
      setIsPremium(t === "pro" || t === "scholar");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const setLocalTier = useCallback(async (next: "free" | "pro" | "scholar") => {
    await setTier(next);
    setTierState(next);
    setIsPremium(next === "pro" || next === "scholar");
  }, []);

  const isUnlocked = useCallback(
    (contentDate: Date, chartId?: string | null) =>
      pureIsUnlocked(contentDate, {
        isPremium,
        isDemoChart: isDemoChart(chartId),
      }),
    [isPremium],
  );

  const openPaywall = useCallback((previewCopy?: string) => {
    setPaywallCopy(previewCopy || DEFAULT_COPY);
    setPaywallOpen(true);
  }, []);

  const closePaywall = useCallback(() => setPaywallOpen(false), []);

  const value = useMemo(
    () => ({
      isPremium,
      tier,
      loading,
      refresh,
      setLocalTier,
      isUnlocked,
      paywallOpen,
      openPaywall,
      closePaywall,
      paywallCopy,
    }),
    [
      isPremium,
      tier,
      loading,
      refresh,
      setLocalTier,
      isUnlocked,
      paywallOpen,
      openPaywall,
      closePaywall,
      paywallCopy,
    ],
  );

  return (
    <EntitlementContext.Provider value={value}>
      {children}
    </EntitlementContext.Provider>
  );
}

export function useEntitlements(): EntitlementValue {
  const ctx = useContext(EntitlementContext);
  if (!ctx) {
    // Safe fallback for pages rendered outside provider (tests / prerender).
    return {
      isPremium: false,
      tier: "free",
      loading: false,
      refresh: async () => {},
      setLocalTier: async () => {},
      isUnlocked: (d, chartId) =>
        pureIsUnlocked(d, { isPremium: false, isDemoChart: isDemoChart(chartId) }),
      paywallOpen: false,
      openPaywall: () => {},
      closePaywall: () => {},
      paywallCopy: DEFAULT_COPY,
    };
  }
  return ctx;
}

/** Re-export pure helpers for non-hook call sites. */
export { isDemoChart };
export type { SubscriptionPlan };
